import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './doc-sheet.css';
import { isEditorFloatingMenuOpen } from '../DocEditor/utils/floatingMenuPosition';
import { isAttachmentPreviewOpen } from '../../utils/attachmentPreviewPresence';
import { setDocSheetOpen } from '../../utils/docSheetPresence';
import { isVariablesPanelOpen } from '../../utils/variablesPanelPresence';
import { useAppMainOverlayHost } from '../../context/AppMainOverlayContext';

/**
 * DocSheet — the bottom sheet every doc opens in (library or mission).
 *
 * Reads as a WINDOW within the main content area (tab container). Close via
 * Esc, backdrop click, or the surface's own close/back chrome ("All Docs" /
 * "Back to mission" / the top-row X).
 *
 * Children receive the animated `close` function (render prop) so inner
 * chrome can close the sheet properly.
 */
const DocSheet = ({ onRequestClose, children }) => {
  const overlayHost = useAppMainOverlayHost();

  // Presence tracks mount lifetime. The sheet is opaque on the first frame,
  // and the canvas stays unmounted for exactly as long as this sheet is.
  useEffect(() => {
    setDocSheetOpen(true);
    return () => setDocSheetOpen(false);
  }, []);

  const close = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  // Esc closes the sheet; inner popups (mention picker / variables panel)
  // win their own Esc so they don't also dismiss this layer
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (isEditorFloatingMenuOpen()) return;
      if (isVariablesPanelOpen()) return;
      if (isAttachmentPreviewOpen()) return;
      close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close]);

  const content = (
    <div className="doc-sheet doc-sheet--open">
      <div className="doc-sheet__backdrop" onClick={close} />
      <div className="doc-sheet__panel">
        <div className="doc-sheet__panel-inner">
          {typeof children === 'function' ? children(close) : children}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, overlayHost || document.body);
};

export default DocSheet;
